// Cache management for AyudaLegal platform
class CacheManager {
  constructor() {
    this.cache = new Map();
    this.cacheExpiry = new Map();
    this.defaultTTL = 5 * 60 * 1000; // 5 minutes default
  }

  set(key, value, ttl = this.defaultTTL) {
    const expiryTime = Date.now() + ttl;
    this.cache.set(key, value);
    this.cacheExpiry.set(key, expiryTime);
  }

  get(key) {
    const expiryTime = this.cacheExpiry.get(key);
    
    if (!expiryTime || Date.now() > expiryTime) {
      this.cache.delete(key);
      this.cacheExpiry.delete(key);
      return null;
    }
    
    return this.cache.get(key);
  }

  clear() {
    this.cache.clear();
    this.cacheExpiry.clear();
  }

  delete(key) {
    this.cache.delete(key);
    this.cacheExpiry.delete(key);
  }

  // Clean expired entries
  cleanup() {
    const now = Date.now();
    for (const [key, expiryTime] of this.cacheExpiry.entries()) {
      if (now > expiryTime) {
        this.cache.delete(key);
        this.cacheExpiry.delete(key);
      }
    }
  }
}

// Global cache instance
const globalCache = new CacheManager();

// Cached API wrapper
export const cachedApiCall = async (cacheKey, apiCall, ttl) => {
  // Check cache first
  const cachedResult = globalCache.get(cacheKey);
  if (cachedResult) {
    return cachedResult;
  }

  try {
    const result = await apiCall();
    globalCache.set(cacheKey, result, ttl);
    return result;
  } catch (error) {
    console.error(`API call failed for key ${cacheKey}:`, error);
    throw error;
  }
};

// Pre-fetch critical data
export const prefetchCriticalData = async (backendUrl) => {
  const criticalEndpoints = [
    { key: 'regions', url: '/api/regions', ttl: 30 * 60 * 1000 }, // 30 minutes
    { key: 'legal_areas', url: '/api/legal-areas', ttl: 30 * 60 * 1000 }
  ];

  const prefetchPromises = criticalEndpoints.map(async ({ key, url, ttl }) => {
    try {
      if (!globalCache.get(key)) {
        const response = await fetch(`${backendUrl}${url}`);
        if (response.ok) {
          const data = await response.json();
          globalCache.set(key, data, ttl);
        }
      }
    } catch (error) {
      console.warn(`Failed to prefetch ${key}:`, error);
    }
  });

  await Promise.allSettled(prefetchPromises);
};

// Cleanup cache periodically
setInterval(() => {
  globalCache.cleanup();
}, 5 * 60 * 1000); // Clean every 5 minutes

export { globalCache };